import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, ArrowLeft, Download } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { useCandidateProfileQuery, useCandidateProfileScore } from '../../hooks/useCandidateProfile';
import { exportCandidateProfilePdf } from '../../api/candidate';
import { toast } from 'sonner';

export function CandidateCvPage() {
  const { data: profileData = {}, isPending: profilePending, isError: profileError } = useCandidateProfileQuery();
  const { data: scorecard, isPending: scorePending, isError: scoreError } = useCandidateProfileScore();
  const [isExporting, setIsExporting] = useState(false);

  const personalInfo = (profileData['personal-info'] ?? [])[0] ?? {};

  const handleExport = useCallback(async () => {
    if (typeof window === 'undefined') return;
    try {
      setIsExporting(true);
      const result = await exportCandidateProfilePdf();
      const blobUrl = URL.createObjectURL(result.blob);
      const anchor = document.createElement('a');
      anchor.href = blobUrl;
      anchor.download = result.fileName;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(blobUrl);
      toast.success(
        result.mimeType === 'application/pdf'
          ? 'Your Lighthouse CV PDF is downloading.'
          : 'Profile export ready (dev text format).',
      );
    } catch (error) {
      console.error(error);
      toast.error('Unable to generate your CV right now. Please try again.');
    } finally {
      setIsExporting(false);
    }
  }, []);

  const headline = personalInfo.headline || personalInfo.summary || 'Add a headline to stand out.';
  const location = personalInfo.location || 'Location not provided';
  const fullName = personalInfo.fullName || 'Your name';

  const completion = scorecard?.score ?? 0;
  const completionLabel =
    completion >= 90 ? 'CV ready' : completion >= 70 ? 'Almost there' : 'Complete your profile for a stronger CV';

  return (
    <main
      className="relative min-h-screen overflow-hidden bg-[radial-gradient(circle_at_6%_10%,rgba(99,102,241,0.18),transparent_52%),linear-gradient(180deg,#030712_0%,#020816_70%,#01020a_100%)]"
      aria-labelledby="cv-page-title"
    >
      <header className="border-b border-white/10 bg-[rgba(5,9,19,0.82)] backdrop-blur-md sticky top-0 z-40">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-4">
          <Link
            to="/candidate/dashboard"
            className="inline-flex items-center gap-2 text-sm text-gray-300 hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to dashboard
          </Link>
          <div className="flex items-center gap-3">
            <Badge className="bg-white/10 text-xs text-gray-200 border-white/20">
              {scorePending ? 'Calculating score…' : `${completion}% profile strength`}
            </Badge>
            <span className="hidden text-xs text-gray-400 sm:inline">{completionLabel}</span>
          </div>
        </div>
      </header>

      <main className="mx-auto flex max-w-5xl flex-col gap-6 px-4 py-8 lg:flex-row">
        <section className="flex-1 space-y-4">
          <Card className="border-white/10 bg-white/[0.03]">
            <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gold-gradient text-black">
                  <FileText className="h-5 w-5" />
                </div>
                <div>
                  <CardTitle className="text-lg text-white">Lighthouse CV preview</CardTitle>
                  <p className="text-xs text-gray-400">
                    This is the information employers will see on your Lighthouse CV.
                  </p>
                </div>
              </div>
              <Button
                type="button"
                className="mt-3 bg-gold-gradient text-black hover:brightness-110 sm:mt-0"
                onClick={() => void handleExport()}
                disabled={isExporting || scorePending}
              >
                <Download className="mr-2 h-4 w-4" />
                {isExporting ? 'Preparing PDF…' : 'Download CV PDF'}
              </Button>
            </CardHeader>
            <CardContent className="space-y-4 border-t border-white/5 bg-black/20 p-6 text-sm text-gray-200">
              {profilePending && <p className="text-gray-400">Loading your profile…</p>}
              {profileError && !profilePending && (
                <p className="text-red-300">We could not load your profile. Please refresh and try again.</p>
              )}
              {!profilePending && !profileError && (
                <>
                  <div>
                    <p className="text-xl font-semibold text-white">{fullName}</p>
                    <p className="text-sm text-gray-300">{headline}</p>
                    <p className="text-xs text-gray-400">{location}</p>
                  </div>
                  <div className="grid gap-4 border-t border-white/10 pt-4 text-xs text-gray-300 sm:grid-cols-2">
                    <div>
                      <p className="text-[0.7rem] uppercase tracking-[0.25em] text-gray-500">Profile strength</p>
                      <p className="mt-1 text-sm text-white">
                        {scorePending || scoreError ? 'Calculating…' : `${completion}%`}
                      </p>
                    </div>
                    <div>
                      <p className="text-[0.7rem] uppercase tracking-[0.25em] text-gray-500">Summary</p>
                      <p className="mt-1">
                        {personalInfo.summary ||
                          'Complete your story and key achievements to help employers understand your journey.'}
                      </p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500">
                    Tip: update your profile sections and rerun this export whenever your experience changes.
                  </p>
                </>
              )}
            </CardContent>
          </Card>
        </section>

        <aside className="w-full space-y-4 lg:w-80">
          <Card className="border-white/10 bg-white/[0.06]">
            <CardHeader>
              <CardTitle className="text-sm text-white">How the Lighthouse CV works</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-xs text-gray-300">
              <p>
                Your CV PDF is generated from your Lighthouse profile and onboarding answers. It&apos;s designed to be
                recruiter-friendly and highlight returner journeys.
              </p>
              <ul className="list-disc space-y-1 pl-4">
                <li>Update profile sections to refresh the PDF.</li>
                <li>No need to upload a separate CV if this covers everything.</li>
                <li>You can still upload a traditional CV in your profile if you prefer.</li>
              </ul>
            </CardContent>
          </Card>
        </aside>
      </main>
    </main>
  );
}
